/**
 * centBasedImplementation.js
 * 
 * Implementación completa de cálculos financieros basados en centavos
 * para el sistema de inventario.
 */

// Constantes para la conversión
const CENTS_MULTIPLIER = 100;
const DECIMAL_PLACES = 2;

/**
 * Convierte un valor decimal a centavos (entero)
 * @param {number} value - Valor decimal a convertir
 * @returns {number} - Valor en centavos (entero)
 */
function toCents(value) {
    // Redondeamos después de multiplicar para asegurar valores enteros correctos
    return Math.round(value * CENTS_MULTIPLIER);
}

/**
 * Convierte un valor en centavos a formato decimal
 * @param {number} cents - Valor en centavos (entero)
 * @returns {number} - Valor decimal con 2 decimales
 */
function fromCents(cents) {
    // Aseguramos un valor con 2 decimales
    return parseFloat((cents / CENTS_MULTIPLIER).toFixed(DECIMAL_PLACES));
}

/**
 * Calcula el costo promedio basado en centavos
 * @param {number} currentStock - Cantidad actual en inventario
 * @param {number} currentCost - Costo actual por unidad
 * @param {number} newQuantity - Cantidad de la nueva entrada
 * @param {number} newCost - Costo de la nueva entrada por unidad
 * @returns {number} - Nuevo costo promedio con 2 decimales
 */
function calculateAverageCostInCents(currentStock, currentCost, newQuantity, newCost) {
    // Convertir valores a centavos
    const currentCostCents = toCents(currentCost);
    const newCostCents = toCents(newCost);
    
    // Realizar cálculos con enteros
    const currentValueCents = currentStock * currentCostCents;
    const newValueCents = newQuantity * newCostCents;
    const totalValueCents = currentValueCents + newValueCents;
    const totalQuantity = currentStock + newQuantity;
    
    // Calcular el costo promedio en centavos
    const averageCostCents = Math.round(totalValueCents / totalQuantity);
    
    // Convertir de vuelta a formato decimal
    return fromCents(averageCostCents);
}

/**
 * Calcula el valor total del inventario basado en centavos
 * @param {number} quantity - Cantidad de unidades
 * @param {number} unitCost - Costo por unidad
 * @returns {number} - Valor total con 2 decimales
 */
function calculateTotalValueInCents(quantity, unitCost) {
    // Convertir costo a centavos
    const unitCostCents = toCents(unitCost);
    
    // Calcular el valor total en centavos
    const totalValueCents = quantity * unitCostCents;
    
    // Convertir de vuelta a formato decimal
    return fromCents(totalValueCents);
}

/**
 * Función auxiliar para redondear a 2 decimales
 * @param {number} value - Valor a redondear
 * @returns {number} - Valor redondeado a 2 decimales
 */
function roundToTwoDecimals(value) {
    return fromCents(toCents(value));
}

/**
 * Suma valores monetarios con precisión de centavos
 * @param  {...number} values - Valores a sumar
 * @returns {number} - Suma total con 2 decimales
 */
function sumMoneyValues(...values) {
    // Convertir todos los valores a centavos
    const centValues = values.map(v => toCents(v));
    
    // Sumar los valores en centavos
    const totalCents = centValues.reduce((sum, val) => sum + val, 0);
    
    // Convertir de vuelta a formato decimal
    return fromCents(totalCents);
}

/**
 * Resta valores monetarios con precisión de centavos
 * @param {number} value1 - Valor del que se resta
 * @param {number} value2 - Valor a restar
 * @returns {number} - Resultado de la resta con 2 decimales
 */
function subtractMoneyValues(value1, value2) {
    // Convertir valores a centavos
    const cents1 = toCents(value1);
    const cents2 = toCents(value2);
    
    // Realizar la resta en centavos
    const resultCents = cents1 - cents2;
    
    // Convertir de vuelta a formato decimal
    return fromCents(resultCents);
}

/**
 * Multiplica un valor monetario por una cantidad con precisión de centavos
 * @param {number} value - Valor monetario
 * @param {number} quantity - Cantidad a multiplicar
 * @returns {number} - Resultado de la multiplicación con 2 decimales
 */
function multiplyMoneyValue(value, quantity) {
    // Convertir valor a centavos
    const valueCents = toCents(value);
    
    // Realizar la multiplicación en centavos
    const resultCents = Math.round(valueCents * quantity);
    
    // Convertir de vuelta a formato decimal
    return fromCents(resultCents);
}

/**
 * Divide un valor monetario por una cantidad con precisión de centavos
 * @param {number} value - Valor monetario
 * @param {number} divisor - Divisor
 * @returns {number} - Resultado de la división con 2 decimales
 */
function divideMoneyValue(value, divisor) {
    if (divisor === 0) {
        throw new Error('División por cero no permitida');
    }
    
    // Convertir valor a centavos
    const valueCents = toCents(value);
    
    // Realizar la división y redondear
    const resultCents = Math.round(valueCents / divisor);
    
    // Convertir de vuelta a formato decimal
    return fromCents(resultCents);
}

// Funciones de compatibilidad con API previa
function calculateAverageCost(currentStock, currentCost, newQuantity, newUnitCost) {
    return calculateAverageCostInCents(currentStock, currentCost, newQuantity, newUnitCost);
}

function calculateCostChangePercentage(previousCost, newCost) {
    const prev = Number(previousCost) || 0;
    const next = Number(newCost) || 0;
    if (prev === 0) {
        // Si no había costo previo, definimos cambio como 100% cuando el nuevo es > 0, o 0 si ambos 0
        return next > 0 ? 100 : 0;
    }
    const pct = ((next - prev) / prev) * 100;
    return Math.round(pct * 100) / 100;
}

// Exportar todas las funciones (CommonJS) para compatibilidad con import nombrado
module.exports = {
    toCents,
    fromCents,
    calculateAverageCostInCents,
    calculateTotalValueInCents,
    roundToTwoDecimals,
    sumMoneyValues,
    subtractMoneyValues,
    multiplyMoneyValue,
    divideMoneyValue,
    // Compat exports
    calculateAverageCost,
    calculateCostChangePercentage
};
